import { AccountInfo, InteractionRequiredAuthError, SilentRequest } from "@azure/msal-browser";
import { msalInstance, sharepointScopes, graphScopes, apiScopes, mailScopes } from "./msalConfig";

/**
 * Acquire an access token for a given resource.
 * Tries silent acquisition first (from cache), falls back to redirect if the
 * user needs to re-authenticate or hasn't consented to the scopes yet.
 */
async function acquireToken(request: SilentRequest): Promise<string> {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    // No signed-in user — trigger full login.
    await msalInstance.loginRedirect(request);
    throw new Error("Redirecting to login...");
  }

  const account: AccountInfo = accounts[0];

  try {
    const result = await msalInstance.acquireTokenSilent({
      ...request,
      account
    });
    return result.accessToken;
  } catch (error) {
    // Redirect interactively on ANY recoverable acquisition failure — not just
    // InteractionRequiredAuthError. monitor_window_timeout (a BrowserAuthError
    // from a blocked silent iframe) must also fall through to redirect, or the
    // whole load fails.
    const code = (error as { errorCode?: string })?.errorCode ?? "";
    const isTimeout =
      code === "monitor_window_timeout" ||
      code === "monitor_window_iframe_timeout" ||
      code === "iframe_closed_prematurely" ||
      code === "block_iframe_reload";
    if (error instanceof InteractionRequiredAuthError || isTimeout) {
      await msalInstance.acquireTokenRedirect(request);
      throw new Error("Redirecting for token acquisition...");
    }
    throw error;
  }
}

/** Get an access token for SharePoint REST API calls. */
export function getSharePointToken(): Promise<string> {
  return acquireToken(sharepointScopes);
}

/**
 * Token for a DIFFERENT SharePoint host in the same tenant — e.g. the user's
 * OneDrive for Business ("…-my.sharepoint.com"). SPO access tokens are
 * audience-scoped per host, but the delegated permission (AllSites.Manage)
 * is granted once for the whole SharePoint Online service, so this acquires
 * silently with the existing consent.
 */
export function getSharePointTokenFor(resourceOrigin: string): Promise<string> {
  return acquireToken({ scopes: [`${resourceOrigin.replace(/\/$/, "")}/AllSites.Manage`] });
}

/**
 * Silent-only SharePoint token — NEVER triggers an interactive redirect.
 * Resolves to null if no session or the scope isn't cached/consented yet.
 * Use on always-loaded surfaces (e.g. the Home's background icon sync) where a
 * redirect would be disruptive; the caller just skips the SharePoint call.
 */
export async function getSharePointTokenSilent(): Promise<string | null> {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) return null;
  try {
    const result = await msalInstance.acquireTokenSilent({ ...sharepointScopes, account: accounts[0] });
    return result.accessToken;
  } catch {
    return null;
  }
}

/** Get an access token for Microsoft Graph API calls. */
export function getGraphToken(): Promise<string> {
  return acquireToken(graphScopes);
}

/**
 * Graph token scoped to read files the signed-in user can access (delegated
 * `Files.Read`). Used by the Production Grid Auto-sync to resolve a pasted
 * OneDrive/SharePoint link through the Graph `/shares` endpoint — that works
 * for every link shape ("copy link" share tokens, address-bar paths, …),
 * unlike the SharePoint server-relative REST call. `Files.Read` is
 * user-consentable (no admin approval) and covers the user's own OneDrive.
 */
export function getGraphFilesToken(): Promise<string> {
  return acquireToken({ scopes: ["Files.Read"] });
}

/**
 * The same Files.Read token, SILENTLY — null when the account has not
 * consented to the scope, instead of navigating the whole app to Microsoft.
 *
 * WHY THIS EXISTS (Elbert Azores at the cutting table, 2026-09-09: BQC2
 * "experiencing unending loading and reloading"). The station screens read the
 * watched workbook on mount, that read asks for Files.Read, and on an account
 * that has never consented acquireTokenSilent throws InteractionRequired — so
 * acquireToken fired acquireTokenRedirect, the page navigated away and came
 * back, the station mounted, and the read asked again. A redirect loop with no
 * exit, on the one screen a cutter has to use all day.
 *
 * The workbook is an OPTIONAL source for these screens and always has been:
 * every caller already handles "the workbook could not be read" and the
 * station works without it. An optional resource must never be able to
 * navigate the app — the same rule getMailTokenSilent above already follows.
 * Interactive consent still belongs on the deliberate actions (Auto-sync
 * setup, Import → From SharePoint), which keep getGraphFilesToken.
 */
export async function getGraphFilesTokenSilent(): Promise<string | null> {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) return null;
  try {
    const result = await msalInstance.acquireTokenSilent({
      scopes: ["Files.Read"], account: accounts[0],
    });
    return result.accessToken;
  } catch {
    return null;
  }
}

/**
 * Write-capable Graph files token (delegated `Files.ReadWrite`) — a SEPARATE
 * scope from `getGraphFilesToken`'s `Files.Read` on purpose: everything that
 * reads the watched workbook (Production Grid Auto-sync) should keep working
 * on a read-only grant even if this one is never consented. Used only by
 * AssigningOutboxService to drop the ASSIGNING outbox CSV beside the watched
 * workbook, in the same OneDrive folder `AutoWriteAssigning.bas` reads it
 * from. First use prompts the signed-in account (in practice, the same
 * person who owns that OneDrive) for one extra consent — after that it's
 * silent like every other scope here.
 */
export function getGraphFilesWriteToken(): Promise<string> {
  return acquireToken({ scopes: ["Files.ReadWrite"] });
}

/**
 * Mail.Send token, silently (from cache). Returns null if the scope hasn't
 * been consented yet — the caller then prompts the user to enable approval
 * emails (which runs the interactive consent below). Never redirects.
 */
export async function getMailTokenSilent(): Promise<string | null> {
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) return null;
  try {
    const result = await msalInstance.acquireTokenSilent({ ...mailScopes, account: accounts[0] });
    return result.accessToken;
  } catch {
    return null;
  }
}

/**
 * One-time interactive consent for Mail.Send, via popup (must be called from
 * a direct user gesture, e.g. an "Enable approval emails" button). After this
 * succeeds, getMailTokenSilent() works for subsequent sends.
 */
export async function consentMail(): Promise<string> {
  const result = await msalInstance.acquireTokenPopup({ ...mailScopes });
  return result.accessToken;
}

/**
 * One-time interactive sign-in for our own API scope, by redirect (call it from
 * a direct user gesture, e.g. a "Grant access" button; it also renews a lapsed
 * session — a SPA refresh token expires after a day). After this
 * succeeds, getApiTokenSilent() works for every later call.
 *
 * WHY THIS IS NEEDED NOW AND WAS NOT BEFORE. getApiTokenSilent is deliberately
 * silent-only, which was right while every /api/* endpoint was fail-open: no
 * token meant a slightly degraded answer. /api/erp/* fails CLOSED, because
 * every row it returns is customer data — and a silent null there means the
 * browser sends no auth header at all, Static Web Apps' own infrastructure
 * token arrives instead, and the Function rejects THAT with a message about an
 * audience of "...azurewebsites.net/azurefunctions". Which is true, and reads
 * like a broken tenant configuration rather than "nobody has consented yet".
 */
export async function consentApi(): Promise<string> {
  // A full-page REDIRECT, not a popup. The popup was the unreliable part: Edge
  // blocks or strips it, the opener times out (monitor_window_timeout) and the
  // button comes back. A redirect needs neither a popup nor a hidden iframe —
  // it leaves for Microsoft, comes back to this same URL, and handleRedirectPromise
  // (msalConfig) caches the tokens before any screen asks for one.
  //
  // Pinned to the account that is signed in HERE (loginHint): without it the
  // machine's whole account picker is offered, and on a shared laptop picking a
  // different one grants access to an account this page never reads a token for.
  const signedIn = apiAccount();
  lastApiTokenIssue = "";
  await msalInstance.acquireTokenRedirect({
    ...apiScopes,
    ...(signedIn ? { account: signedIn, loginHint: signedIn.username } : {}),
  });
  // Unreached in practice: the page navigates away. Callers that go on to load
  // data simply find nothing to do on a page that is already leaving.
  return "";
}

/** Get the currently signed-in user's account info (or null). */
export function getCurrentAccount(): AccountInfo | null {
  const accounts = msalInstance.getAllAccounts();
  return accounts[0] ?? null;
}

/**
 * Best-effort token for calling our own Functions API (`/api/*`).
 *
 * Unlike the resource getters above, this NEVER triggers an interactive
 * redirect — if the API scope isn't exposed/consented yet (or there's no
 * session), it resolves to null and the caller simply omits the auth header.
 * That keeps the app working while the API is in fail-open mode, and starts
 * sending valid tokens automatically once the scope is configured.
 */
export async function getApiTokenSilent(): Promise<string | null> {
  const account = apiAccount();
  if (!account) {
    lastApiTokenIssue = "no_account";
    return null;
  }
  try {
    const result = await msalInstance.acquireTokenSilent({ ...apiScopes, account });
    lastApiTokenIssue = "";
    return result.accessToken;
  } catch (error) {
    const e = error as { errorCode?: string; subError?: string };
    lastApiTokenIssue = [e?.errorCode, e?.subError].filter(Boolean).join("/") || "unknown";
    return null;
  }
}

/**
 * Why the last getApiTokenSilent / consentApi came back without a token —
 * an MSAL error code (e.g. "consent_required", "interaction_required",
 * "no_tokens_found", "wrong_account:…") or "" when it last succeeded. Shown
 * beside the "grant access" message so a loop can be diagnosed from a
 * screenshot instead of guessed at.
 */
export function apiTokenIssue(): string {
  return lastApiTokenIssue;
}

let lastApiTokenIssue = "";

/**
 * The account the API token is read for: the one MSAL marks active, else the
 * first cached. The consent popup is pinned to the same one, so what was
 * granted is what is later read.
 */
function apiAccount(): AccountInfo | null {
  return msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
}
